import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { membersAPI, historyAPI, getImageUrl } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';
import Input from '../components/common/Input';

export default function Members() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [members, setMembers] = useState([]);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    loadMembers();
    loadHistory();
  }, []);

  const loadMembers = async () => {
    try {
      setIsLoading(true);
      const response = await membersAPI.getAll();
      setMembers(response.members || []);
    } catch (err) {
      console.error('Error loading members:', err);
      setError('Failed to load members');
    } finally {
      setIsLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const entries = await historyAPI.getAll();
      setHistoryEntries(Array.isArray(entries) ? entries : []);
    } catch (err) {
      console.error('Error loading history:', err);
      // Don't show error to user - just means no championships to display
    }
  };

  // Map member nicknames to full names used in history entries
  const getFullName = (nickname) => {
    const nameMap = {
      'Ant': 'Anthony Sullivan',
      'Gaz': 'Gareth Rees',
      'Bazza': 'Barry Newbury',
      'Beast': 'Paul Haley',
      'Dazzler': 'Darren Heywood-Rodgers',
      'Bryn': 'Bryn Hurle',
      'Rolfey': 'Jonathan Rolfe',
    };
    return nameMap[nickname] || nickname;
  };

  const getMemberChampionships = (memberName) => {
    const fullName = getFullName(memberName);
    return historyEntries
      .filter(entry => {
        // Handle both exact matches and variations (e.g., "Darren Haywood-Rodgers" vs "Darren Heywood-Rodgers")
        if (entry.champion_name === fullName || entry.champion_name === memberName) {
          return true;
        }
        // Handle Dazzler's name variations
        if (memberName === 'Dazzler' &&
            (entry.champion_name.includes('Darren') && entry.champion_name.includes('Rodgers'))) {
          return true;
        }
        return false;
      })
      .sort((a, b) => b.year - a.year); // Sort by year descending (most recent first)
  };

  const getMemberBowlWins = (memberName) => {
    const fullName = getFullName(memberName);
    return historyEntries
      .filter(entry => {
        // Check if bowl_winner_name matches
        if (entry.bowl_winner_name === fullName || entry.bowl_winner_name === memberName) {
          return true;
        }
        // Handle Dazzler's name variations
        if (memberName === 'Dazzler' &&
            entry.bowl_winner_name &&
            (entry.bowl_winner_name.includes('Darren') && entry.bowl_winner_name.includes('Rodgers'))) {
          return true;
        }
        return false;
      })
      .sort((a, b) => b.year - a.year); // Sort by year descending (most recent first)
  };

  const handleEditProfile = (member) => {
    setEditingMember(member);
    setShowEditModal(true);
  };

  const handleSubmit = async (formData, photoFile) => {
    try {
      setIsSubmitting(true);
      setError('');

      let memberId;

      if (editingMember) {
        // Update existing member profile
        await membersAPI.update(editingMember.id, formData);
        memberId = editingMember.id;
      } else {
        // Create new member profile
        const newMember = await membersAPI.create(formData);
        memberId = newMember.member.id;
      }

      // Upload photo if one was selected
      if (photoFile) {
        try {
          await membersAPI.uploadPhoto(memberId, photoFile);
        } catch (uploadErr) {
          console.error('Error uploading photo:', uploadErr);
          // Member was saved, but photo upload failed
          // Close modal and reload, then show error
          setShowEditModal(false);
          setEditingMember(null);
          await loadMembers();
          setError('Member profile saved, but photo upload failed. Please edit the member and try uploading the photo again.');
          setIsSubmitting(false);
          return; // Exit early to show error
        }
      }

      setShowEditModal(false);
      setEditingMember(null);
      await loadMembers();
    } catch (err) {
      console.error('Error saving member:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save member profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (member) => {
    if (!window.confirm(`Are you sure you want to delete ${member.name}'s profile? This cannot be undone.`)) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      await membersAPI.delete(member.id);
      // Only close modal and reload if successful
      setShowEditModal(false);
      setEditingMember(null);
      await loadMembers();
    } catch (err) {
      console.error('Error deleting member:', err);
      // Show error message but keep modal open
      const errorMessage = err.response?.data?.error || err.response?.data?.message;
      setError(errorMessage || 'Failed to delete member profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="w-full pb-20">
      {/* Header */}
      <div className="mb-4 px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">The Honourable Members</h1>
      </div>

      {error && !showEditModal && (
        <div className="bg-red-600 text-white px-4 py-3 mb-4 mx-4 md:mx-8 lg:mx-12 max-w-7xl mx-auto shadow-lg">
          <div className="flex items-start gap-2">
            <span className="text-xl">⚠️</span>
            <div className="flex-1">
              <p className="font-semibold mb-1">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        {members.length === 0 ? (
          <div className="bg-white border border-gray-300 rounded-lg p-8">
            <div className="text-center py-12">
              <div className="text-6xl mb-4">👥</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No Members</h2>
              <p className="text-lg text-gray-600">
                Member profiles will appear here once added.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {members.map((member) => (
              <div
                key={member.id}
                onClick={editMode ? () => handleEditProfile(member) : undefined}
                className={`w-full h-full bg-white border border-gray-300 px-4 py-4 transition-colors text-left flex flex-col items-start relative ${
                  editMode ? 'cursor-pointer hover:bg-gray-50' : ''
                }`}
              >
                {/* Edit Icon - Only visible in edit mode */}
                {editMode && (
                  <div className="absolute top-4 right-4">
                    <svg
                      className="w-5 h-5 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg>
                  </div>
                )}
              {/* Roundel and Name Block */}
              <div className="flex items-center gap-4 mb-4">
                {/* Profile Image Roundel */}
                {member.photo_url ? (
                  <img
                    src={getImageUrl(member.photo_url)}
                    alt={member.name}
                    onClick={(e) => {
                      e.stopPropagation();
                      setLightboxImage(getImageUrl(member.photo_url));
                    }}
                    className="w-20 h-20 rounded-full object-cover border-4 border-primary shadow-lg flex-shrink-0 cursor-pointer hover:border-secondary transition-colors"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center border-4 border-primary shadow-lg flex-shrink-0">
                    <span className="text-3xl text-white font-bold">
                      {member.name.charAt(0)}
                    </span>
                  </div>
                )}

                {/* Player Name */}
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {member.name}
                  </h2>
                </div>
              </div>

              {/* Biography */}
              {member.bio && (
                <div className="mt-3 space-y-2">
                  {member.bio.split('\n\n').map((paragraph, index) => (
                    <p key={index} className="text-sm text-gray-700 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              )}

              {/* Championship Wins List */}
              {(() => {
                const championships = getMemberChampionships(member.name);
                return championships.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                      Championship Wins
                    </div>
                    <div className="space-y-2">
                      {championships.map((championship) => (
                        <div
                          key={championship.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/history/${championship.year}`);
                          }}
                          className="flex items-center gap-3 py-2 px-2 -mx-2 rounded hover:bg-gray-100 transition-colors cursor-pointer"
                        >
                          {/* Claret Jug Icon */}
                          <img
                            src="/claret-jug.png"
                            alt="Championship"
                            className="w-6 h-6 object-contain flex-shrink-0"
                          />
                          {/* Year and Venue - Single Line */}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-gray-900 truncate">
                              <span className="font-bold">{championship.year}</span>
                              {' '}
                              <span className="text-gray-600">{championship.course_name}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Bowl Wins List */}
              {(() => {
                const bowlWins = getMemberBowlWins(member.name);
                return bowlWins.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                      Bowl Wins
                    </div>
                    <div className="space-y-2">
                      {bowlWins.map((bowl) => (
                        <div
                          key={bowl.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/history/${bowl.year}`);
                          }}
                          className="flex items-center gap-3 py-2 px-2 -mx-2 rounded hover:bg-gray-100 transition-colors cursor-pointer"
                        >
                          {/* Bowl Trophy Icon */}
                          <img
                            src="/trophy-shield.svg"
                            alt="The Bowl"
                            className="w-6 h-6 object-contain flex-shrink-0"
                          />
                          {/* Year and Venue - Single Line */}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-gray-900 truncate">
                              <span className="font-bold">{bowl.year}</span>
                              {' '}
                              <span className="text-gray-600">{bowl.course_name}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
        )}

      </div>

      {/* Admin Controls - Moved to Bottom */}
      {isAuthenticated && (
        <div className="mt-8 px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
          <div className="bg-white border border-gray-300 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Manage Members</h2>
              <div className="flex gap-3">
                <button
                  onClick={() => setEditMode(!editMode)}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                    editMode
                      ? 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {editMode ? 'Disable Edit Mode' : 'Enable Edit Mode'}
                </button>
                <button
                  onClick={() => {
                    setEditingMember(null);
                    setShowEditModal(true);
                  }}
                  className="px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-secondary transition-colors"
                >
                  + Add Member Profile
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingMember(null);
          setError('');
        }}
        title={editingMember ? `Edit ${editingMember.name}'s Profile` : 'Add Member Profile'}
        size="lg"
      >
          {error && (
            <div className="bg-red-100 text-red-800 px-4 py-3 border border-red-300 mb-4 rounded">
              {error}
            </div>
          )}
        <MemberProfileForm
          member={editingMember}
          onSubmit={handleSubmit}
          onDelete={() => handleDelete(editingMember)}
          onCancel={() => {
            setShowEditModal(false);
            setEditingMember(null);
            setError('');
          }}
          isLoading={isSubmitting}
        />
      </Modal>

      {/* Image Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black p-4"
          onClick={() => setLightboxImage(null)}
        >
          <img
            src={lightboxImage}
            alt="Member photo"
            className="max-w-full max-h-[90vh] w-auto h-auto object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white bg-black bg-opacity-70 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-90 transition-opacity"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

// Member Profile Edit Form Component
function MemberProfileForm({ member, onSubmit, onDelete, onCancel, isLoading }) {
  const [formData, setFormData] = useState({
    name: member?.name || '',
    photo_url: member?.photo_url || '',
    bio: member?.bio || '',
    victories: member?.victories || 0
  });

  const [errors, setErrors] = useState({});
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;
    if (name === 'victories') {
      // Allow empty string or convert to number
      processedValue = value === '' ? '' : parseInt(value) || 0;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedPhoto(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setSelectedPhoto(null);
    setPhotoPreview(null);
  };

  const validate = () => {
    const newErrors = {};

    // Name validation (required for all)
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    const victoriesValue = formData.victories === '' ? 0 : formData.victories;
    if (victoriesValue < 0) {
      newErrors.victories = 'Victories cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validate()) {
      // Convert empty strings to proper defaults before submitting
      const submitData = {
        ...formData,
        victories: formData.victories === '' ? 0 : formData.victories
      };
      onSubmit(submitData, selectedPhoto);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Name - Always shown and editable */}
      <Input
        label="Name"
        name="name"
        value={formData.name}
        onChange={handleChange}
        error={errors.name}
        placeholder="Enter member name"
        required
        autoFocus
      />

      {/* Photo Upload Section */}
      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Profile Photo
        </label>

        {/* Current or Preview Photo */}
        {(photoPreview || formData.photo_url) && (
          <div className="mb-3">
            <img
              src={photoPreview || getImageUrl(formData.photo_url)}
              alt="Preview"
              className="w-32 h-32 rounded-full object-cover border-4 border-primary shadow-lg"
            />
          </div>
        )}

        {/* Photo URL Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Photo Path
          </label>
          <input
            name="photo_url"
            value={formData.photo_url}
            onChange={handleChange}
            placeholder="/images/members/filename.jpg"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <p className="text-sm text-gray-600 mt-2">
            Add photos to <code className="bg-gray-100 px-1 py-0.5 rounded">Assets/members/</code> folder, then enter: <code className="bg-gray-100 px-1 py-0.5 rounded">/images/members/filename.jpg</code>
          </p>
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Biography
        </label>
        <textarea
          name="bio"
          value={formData.bio}
          onChange={handleChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
          rows="6"
          placeholder="Enter member biography"
        />
        <p className="text-xs text-gray-600 mt-1">
          Tip: Use two line breaks (press Enter twice) to create new paragraphs
        </p>
        {errors.bio && (
          <p className="mt-1 text-sm text-red-600">{errors.bio}</p>
        )}
      </div>

      <Input
        label="Victories"
        name="victories"
        type="number"
        min="0"
        value={formData.victories}
        onChange={handleChange}
        error={errors.victories}
        placeholder="Number of championship wins"
      />

      {/* Delete Button - Only show when editing existing member */}
      {member && (
        <div className="mb-4 pt-4 border-t border-gray-200">
          <Button
            type="button"
            onClick={onDelete}
            disabled={isLoading}
            className="w-full bg-red-600 hover:bg-red-700 text-white"
          >
            Delete Member Profile
          </Button>
        </div>
      )}

      <div className="flex gap-3 mt-6">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={isLoading}
          className="flex-1"
        >
          {isLoading ? 'Saving...' : member ? 'Update Profile' : 'Create Member'}
        </Button>
      </div>
    </form>
  );
}
